import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ServerInterface } from "src/store/mine_craft/serversSlice";
import {
  fetchServerBackups, createServerBackup, restoreServerBackup,
  destroyServerBackup, fetchServerBackupDownload
} from "src/actions/mine_craft/serversActions";
import { useAppDispatch } from "src/store/hooks";
import usePermissions from "src/hooks/usePermissions";

import { Button, Input, message, Modal, Popconfirm, Switch, Tag } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloudDownloadOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
  InboxOutlined,
  LockOutlined,
  PlusOutlined,
  SyncOutlined,
  UndoOutlined
} from '@ant-design/icons';
import Card from "src/componets/Card";
import AlertBlock from "src/componets/AlertBlock";
import { gigabytes, shortFromNow } from "./_serverHelpers";

const NAME_MAX = 64;
// A backup takes tens of seconds; poll only while one is still running.
const RUNNING_POLL_MS = 5000;

interface Backup {
  id: string;
  name: string;
  sizeBytes: number;
  successful: boolean;
  locked: boolean;
  createdAt: string;
  completedAt: string | null;
}

const errorOf = (payload: any, fallback: string) =>
  (Array.isArray(payload) ? payload[0] : payload?.errors?.[0]) || fallback;

const _Backups = ({ server }: { server: ServerInterface | undefined }) => {
  const dispatch = useAppDispatch();
  const { write_minecraft } = usePermissions();
  const hosted = server?.hosted_server;

  const [backups, setBackups] = useState<Backup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [name, setName] = useState('');
  // A ref, not state: Modal.confirm captures its onOk once, so a state
  // update from the switch would never reach the confirmed call.
  const truncate = useRef(false);

  const serverId = server?.id;

  const load = useCallback(async (silent = false) => {
    if (!serverId) return;

    if (!silent) setLoading(true);
    const resultAction = await dispatch(fetchServerBackups(serverId));
    if (!silent) setLoading(false);

    if (fetchServerBackups.fulfilled.match(resultAction)) {
      setBackups(resultAction.payload?.backups || []);
      setError(null);
    } else if (!silent) {
      setError(errorOf(resultAction.payload, 'Backups could not be loaded'));
    }
  }, [serverId, dispatch]);

  const isHosted = !!hosted;

  useEffect(() => {
    if (!isHosted) return;

    load().catch(console.error);
  }, [isHosted, load]);

  const running = backups.some(backup => !backup.completedAt);

  useEffect(() => {
    if (!running || !isHosted) return;

    const timer = setInterval(() => load(true).catch(console.error), RUNNING_POLL_MS);

    return () => clearInterval(timer);
  }, [running, isHosted, load]);

  if (!server || !hosted) {
    return (
      <Card>
        <div className='v-srv-empty'>
          <InboxOutlined className='o-icon' />
          <div className='o-title'>Not hosted through crafty-servers</div>
          <div className='o-hint'>Backups are only available for hosted servers.</div>
        </div>
      </Card>
    );
  }

  async function run(key: string, thunk: any, args: any, success: string) {
    setBusy(key);
    const resultAction = await dispatch(thunk(args));
    setBusy(null);

    if (thunk.fulfilled.match(resultAction)) {
      message.success(success);
      load().catch(console.error);
      return resultAction.payload;
    }

    message.error(errorOf(resultAction.payload, `${success.replace(/ed$/, '')} failed`));
    return null;
  }

  async function create() {
    if (!server) return;

    const payload = await run(
      'create',
      createServerBackup,
      { id: server.id, name: name.trim() || undefined },
      'Backup started'
    );

    if (payload) setName('');
  }

  function confirmRestore(backup: Backup) {
    if (!server) return;

    truncate.current = false;

    Modal.confirm({
      title: `Restore “${backup.name}”?`,
      icon: <ExclamationCircleFilled />,
      content: (
        <>
          <p>The server has to be stopped, and its current world is replaced by this backup.</p>
          <label className='v-srv-truncate'>
            <Switch size='small' onChange={value => { truncate.current = value }} />
            <span>Delete all existing files first (truncate)</span>
          </label>
        </>
      ),
      okText: 'Restore',
      okButtonProps: { danger: true },
      onOk: () => run(
        `restore-${backup.id}`,
        restoreServerBackup,
        { id: server.id, backupId: backup.id, truncate: truncate.current },
        'Restore started'
      )
    });
  }

  // Signed and short-lived, so the link is fetched on the click and opened
  // straight away rather than rendered into the row.
  async function download(backup: Backup) {
    if (!server) return;

    setBusy(`download-${backup.id}`);
    const resultAction = await dispatch(fetchServerBackupDownload({ id: server.id, backupId: backup.id }));
    setBusy(null);

    if (fetchServerBackupDownload.fulfilled.match(resultAction) && resultAction.payload?.url) {
      window.open(resultAction.payload.url, '_blank', 'noopener');
    } else {
      message.error(errorOf(resultAction.payload, 'Download link unavailable'));
    }
  }

  const State = ({ backup }: { backup: Backup }) => {
    if (!backup.completedAt) return <Tag icon={<SyncOutlined spin />} color='processing'>running</Tag>;
    if (backup.successful)   return <Tag icon={<CheckCircleOutlined />} color='success'>ready</Tag>;

    return <Tag icon={<CloseCircleOutlined />} color='error'>failed</Tag>;
  };

  return (
    <>
      {write_minecraft &&
        <Card className='v-srv-backup-new'>
          <h4 className='v-content-title'>Create backup</h4>

          <div className='o-row'>
            <Input
              placeholder='Optional name, e.g. before 1.21.40'
              value={name}
              maxLength={NAME_MAX}
              disabled={busy === 'create'}
              onChange={event => setName(event.target.value)}
              onPressEnter={create}
            />
            <Button
              type='primary'
              icon={<PlusOutlined />}
              loading={busy === 'create'}
              onClick={create}
            >
              Create
            </Button>
          </div>

          <div className='o-hint'>Runs in the background and takes tens of seconds.</div>
        </Card>
      }

      <Card className='v-srv-backups'>
        <div className='o-head'>
          <h4 className='v-content-title'>Backups</h4>
          <span className='o-count'>{backups.length}</span>

          <Button
            className='o-icon-btn'
            icon={<SyncOutlined />}
            title='Refresh'
            aria-label='Refresh backups'
            loading={loading}
            onClick={() => load()}
          />
        </div>

        {error &&
          <AlertBlock type='error' message={error} onClose={() => setError(null)} />
        }

        <div className='o-list'>
          {loading && !backups.length &&
            [0, 1, 2].map(index => <div key={index} className='v-srv-backup o-skeleton' />)
          }

          {backups.map(backup => (
            <div key={backup.id} className='v-srv-backup'>
              <div className='o-main'>
                <div className='o-name'>
                  {backup.name}
                  {backup.locked && <LockOutlined className='o-lock' title='Locked — cannot be deleted' />}
                </div>
                <div className='o-meta'>
                  <State backup={backup} />
                  <span>{gigabytes(backup.sizeBytes)}</span>
                  <span title={backup.createdAt}>{shortFromNow(backup.createdAt)}</span>
                </div>
              </div>

              <div className='o-actions'>
                <Button
                  size='small'
                  icon={<CloudDownloadOutlined />}
                  disabled={!backup.completedAt || !backup.successful}
                  loading={busy === `download-${backup.id}`}
                  onClick={() => download(backup)}
                >
                  Download
                </Button>

                {write_minecraft &&
                  <>
                    <Button
                      size='small'
                      icon={<UndoOutlined />}
                      disabled={!backup.completedAt || !backup.successful}
                      loading={busy === `restore-${backup.id}`}
                      onClick={() => confirmRestore(backup)}
                    >
                      Restore
                    </Button>

                    <Popconfirm
                      title='Delete this backup? This cannot be undone.'
                      okText='Delete'
                      okButtonProps={{ danger: true }}
                      disabled={backup.locked}
                      onConfirm={() => run(
                        `delete-${backup.id}`,
                        destroyServerBackup,
                        { id: server.id, backupId: backup.id },
                        'Backup deleted'
                      )}
                    >
                      <Button
                        size='small'
                        danger
                        icon={<DeleteOutlined />}
                        disabled={backup.locked}
                        loading={busy === `delete-${backup.id}`}
                        title={backup.locked ? 'Locked backups cannot be deleted' : undefined}
                      />
                    </Popconfirm>
                  </>
                }
              </div>
            </div>
          ))}
        </div>

        {!loading && !error && !backups.length &&
          <div className='v-srv-empty'>
            <InboxOutlined className='o-icon' />
            <div className='o-title'>No backups yet</div>
            <div className='o-hint'>
              {write_minecraft ? 'Create one above — it runs in the background.' : 'Nothing has been backed up for this server.'}
            </div>
          </div>
        }
      </Card>
    </>
  );
}

export default _Backups;
